import React, { useState, useMemo } from "react";
import {
  Box, Typography, TextField, Accordion, AccordionSummary, AccordionDetails,
  InputAdornment, Button, Chip, useTheme, IconButton,
} from "@mui/material";
import {
  LuSearch, LuChevronDown, LuChevronRight, LuRotateCcw, LuGlobe, LuBuilding2,
  LuLayers, LuRuler, LuCheck, LuFileText, LuCalendar, LuSlidersHorizontal,
} from "react-icons/lu";
import { useTranslation } from "react-i18next";
import FilterBoxGroup from "../../components/input/FilterBoxGroup";
import SquareCheckBox from "../../components/input/SquareCheckBox";
import { leeds } from "../../components/LEED_categories";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

// Full list of unique countries available in the product_epd database table
const DB_COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
  { code: "NO", name: "Norway", flag: "🇳🇴" },
  { code: "FI", name: "Finland", flag: "🇫🇮" },
  { code: "DK", name: "Denmark", flag: "🇩🇰" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
  { code: "TR", name: "Turkey", flag: "🇹🇷" },
  { code: "CN", name: "China", flag: "🇨🇳" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "KR", name: "South Korea", flag: "🇰🇷" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "IE", name: "Ireland", flag: "🇮🇪" },
  { code: "AT", name: "Austria", flag: "🇦🇹" },
  { code: "BE", name: "Belgium", flag: "🇧🇪" },
  { code: "CH", name: "Switzerland", flag: "🇨🇭" },
  { code: "PL", name: "Poland", flag: "🇵🇱" },
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪" },
  { code: "AL", name: "Albania", flag: "🇦🇱" },
  { code: "AR", name: "Argentina", flag: "🇦🇷" },
  { code: "BA", name: "Bosnia and Herzegovina", flag: "🇧🇦" },
  { code: "BD", name: "Bangladesh", flag: "🇧🇩" },
  { code: "BG", name: "Bulgaria", flag: "🇧🇬" },
  { code: "BH", name: "Bahrain", flag: "🇧🇭" },
  { code: "BY", name: "Belarus", flag: "🇧🇾" },
  { code: "CL", name: "Chile", flag: "🇨🇱" },
  { code: "CO", name: "Colombia", flag: "🇨🇴" },
  { code: "CR", name: "Costa Rica", flag: "🇨🇷" },
  { code: "CZ", name: "Czech Republic", flag: "🇨🇿" },
  { code: "DO", name: "Dominican Republic", flag: "🇩🇴" },
  { code: "DZ", name: "Algeria", flag: "🇩🇿" },
  { code: "EC", name: "Ecuador", flag: "🇪🇨" },
  { code: "EE", name: "Estonia", flag: "🇪🇪" },
  { code: "EG", name: "Egypt", flag: "🇪🇬" },
  { code: "GR", name: "Greece", flag: "🇬🇷" },
  { code: "HK", name: "Hong Kong", flag: "🇭🇰" },
  { code: "HR", name: "Croatia", flag: "🇭🇷" },
  { code: "HU", name: "Hungary", flag: "🇭🇺" },
  { code: "ID", name: "Indonesia", flag: "🇮🇩" },
  { code: "IL", name: "Israel", flag: "🇮🇱" },
  { code: "IS", name: "Iceland", flag: "🇮🇸" },
  { code: "KP", name: "North Korea", flag: "🇰🇵" },
  { code: "KW", name: "Kuwait", flag: "🇰🇼" },
  { code: "LK", name: "Sri Lanka", flag: "🇱🇰" },
  { code: "LT", name: "Lithuania", flag: "🇱🇹" },
  { code: "LU", name: "Luxembourg", flag: "🇱🇺" },
  { code: "LV", name: "Latvia", flag: "🇱🇻" },
  { code: "MA", name: "Morocco", flag: "🇲🇦" },
  { code: "MD", name: "Moldova", flag: "🇲🇩" },
  { code: "MU", name: "Mauritius", flag: "🇲🇺" },
  { code: "MY", name: "Malaysia", flag: "🇲🇾" },
  { code: "MZ", name: "Mozambique", flag: "🇲🇿" },
  { code: "OM", name: "Oman", flag: "🇴🇲" },
  { code: "PA", name: "Panama", flag: "🇵🇦" },
  { code: "PH", name: "Philippines", flag: "🇵🇭" },
  { code: "PT", name: "Portugal", flag: "🇵🇹" },
  { code: "QA", name: "Qatar", flag: "🇶🇦" },
  { code: "RO", name: "Romania", flag: "🇷🇴" },
  { code: "RS", name: "Serbia", flag: "🇷🇸" },
  { code: "RU", name: "Russia", flag: "🇷🇺" },
  { code: "SA", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "SG", name: "Singapore", flag: "🇸🇬" },
  { code: "SI", name: "Slovenia", flag: "🇸🇮" },
  { code: "SK", name: "Slovakia", flag: "🇸🇰" },
  { code: "SL", name: "Sierra Leone", flag: "🇸🇱" },
  { code: "TH", name: "Thailand", flag: "🇹🇭" },
  { code: "TW", name: "Taiwan", flag: "🇹🇼" },
  { code: "UA", name: "Ukraine", flag: "🇺🇦" },
  { code: "VN", name: "Vietnam", flag: "🇻🇳" },
  { code: "YE", name: "Yemen", flag: "🇾🇪" },
];

const REGION_LIST = ["Europe", "Nordics", "North America", "Asia", "Oceania", "South America", "Africa"];

const STANDARDS_LIST = ["EN 15804+A1", "EN 15804+A2", "ISO 14040 / 14044", "ISO 21930", "ISO 14025"];

const DEFAULT_PROGRAM_OPERATORS = [
  "AFNOR", "AENOR", "Association of Issuing Bodies", "ASTM",
  "Athenasmi", "AusLCI", "Australasian EPD System", "B-EPD",
  "IBU - Institut Bauen und Umwelt", "EPD International", "UL Environment",
];

const EPDFiltersSidebar = ({
  searchQuery,
  setSearchQuery,
  allManufacturers = [],
  selectedManufacturers = [],
  setSelectedManufacturers,
  selectedCategory,
  setSelectedCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  selectedUnit,
  setSelectedUnit,
  selectedPrograms = [],
  setSelectedPrograms,
  selectedRegions = [],
  setSelectedRegions,
  selectedCountries = [],
  setSelectedCountries,
  selectedStandards = [],
  setSelectedStandards,
  selectedYears = [],
  setSelectedYears,
  availableYears = [],
  onResetAll,
  onApplyFilters,
  epds = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [manufacturerSearch, setManufacturerSearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [programSearch, setProgramSearch] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [showAllManufacturers, setShowAllManufacturers] = useState(false);
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Dynamically compute unique normalized units from active EPD data
  const dynamicUnitOptions = useMemo(() => {
    const rawUnits = epds.map((item) => item.normalized_unit || item.declared_unit).filter(Boolean);
    const uniqueRaw = [...new Set(rawUnits)].map((u) => {
      const uL = u.toLowerCase().trim();
      if (uL === "m2") return "m²";
      if (uL === "m3") return "m³";
      return u;
    });

    const standardList = ["kg", "m", "m²", "m³", "unit", "ton", "l", "kWh", "MJ", "piece"];
    const combined = Array.from(new Set([...standardList, ...uniqueRaw]));

    return combined.map((unit) => {
      let subtitle = "Other";
      const u = unit.toLowerCase();
      if (u === "kg" || u.includes("ton")) subtitle = t("Mass");
      else if (u === "m²" || u.includes("sq")) subtitle = t("Area");
      else if (u === "m³" || u.includes("cu")) subtitle = t("Volume");
      else if (u === "m") subtitle = t("Length");
      else if (u === "unit" || u === "piece") subtitle = t("Piece");
      else if (u === "kwh" || u === "mj") subtitle = t("Energy");
      return { value: unit, label: unit, subtitle };
    });
  }, [epds, t]);

  const countryOptions = useMemo(() => {
    return DB_COUNTRIES.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const filteredManufacturers = useMemo(() => {
    const term = manufacturerSearch.trim().toLowerCase();
    const list = allManufacturers.map((m) => {
      const name = typeof m === "string" ? m : m.manufacturer || "";
      const count = typeof m === "object" ? m.count : null;
      return { name, count };
    });

    if (!term) {
      return showAllManufacturers ? list : list.slice(0, 10);
    }
    return list.filter((m) => m.name.toLowerCase().includes(term)).slice(0, 20);
  }, [allManufacturers, manufacturerSearch, showAllManufacturers]);

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return leeds;
    return leeds.filter(
      (c) =>
        c.label.toLowerCase().includes(categorySearch.toLowerCase()) ||
        c.sub_categories?.some((s) => s.label.toLowerCase().includes(categorySearch.toLowerCase()))
    );
  }, [categorySearch]);

  const filteredPrograms = useMemo(() => {
    if (!programSearch.trim()) {
      return showAllPrograms ? DEFAULT_PROGRAM_OPERATORS : DEFAULT_PROGRAM_OPERATORS.slice(0, 8);
    }
    return DEFAULT_PROGRAM_OPERATORS.filter((p) =>
      p.toLowerCase().includes(programSearch.toLowerCase())
    );
  }, [programSearch, showAllPrograms]);

  const filteredCountries = useMemo(() => {
    const term = countrySearch.trim().toLowerCase();
    if (!term) {
      return showAllCountries ? countryOptions : countryOptions.slice(0, 8);
    }
    return countryOptions.filter((c) =>
      c.name.toLowerCase().includes(term) || c.code.toLowerCase().includes(term)
    );
  }, [countryOptions, countrySearch, showAllCountries]);

  const handleManufacturerToggle = (mName) => {
    setSelectedManufacturers(
      selectedManufacturers.includes(mName)
        ? selectedManufacturers.filter((x) => x !== mName)
        : [...selectedManufacturers, mName]
    );
  };

  const handleProgramToggle = (pName) => {
    setSelectedPrograms(
      selectedPrograms.includes(pName)
        ? selectedPrograms.filter((x) => x !== pName)
        : [...selectedPrograms, pName]
    );
  };

  const handleRegionToggle = (rName) => {
    setSelectedRegions(
      selectedRegions.includes(rName)
        ? selectedRegions.filter((x) => x !== rName)
        : [...selectedRegions, rName]
    );
  };

  const handleCountryToggle = (cCode) => {
    setSelectedCountries(
      selectedCountries.includes(cCode)
        ? selectedCountries.filter((x) => x !== cCode)
        : [...selectedCountries, cCode]
    );
  };

  const handleStandardToggle = (std) => {
    setSelectedStandards(
      selectedStandards.includes(std)
        ? selectedStandards.filter((x) => x !== std)
        : [...selectedStandards, std]
    );
  };

  const handleYearToggle = (yr) => {
    setSelectedYears(
      selectedYears.includes(yr)
        ? selectedYears.filter((x) => x !== yr)
        : [...selectedYears, yr]
    );
  };

  const toggleCategoryExpand = (catKey, e) => {
    e.stopPropagation();
    setExpandedCategories(
      expandedCategories.includes(catKey)
        ? expandedCategories.filter((k) => k !== catKey)
        : [...expandedCategories, catKey]
    );
  };

  const activeFiltersCount =
    (searchQuery ? 1 : 0) +
    selectedManufacturers.length +
    (selectedCategory ? 1 : 0) +
    (selectedSubCategory ? 1 : 0) +
    (selectedUnit ? 1 : 0) +
    selectedPrograms.length +
    selectedRegions.length +
    selectedCountries.length +
    selectedStandards.length +
    selectedYears.length;

  const accordionStyle = {
    backgroundColor: "transparent",
    boxShadow: "none",
    "&:before": { display: "none" },
    borderBottom: `1px solid ${tok.border.divider}`,
    "&.Mui-expanded": { margin: 0 },
    mb: 0.75,
  };

  const accordionSummaryStyle = {
    px: 3.5,
    py: 1.75,
    minHeight: "56px",
    "&.Mui-expanded": { minHeight: "56px" },
    "& .MuiAccordionSummary-content": { my: 0.5 },
  };

  const sectionIconColor = isDark ? "#4ade80" : "#16a34a";
  const sectionHeaderSx = {
    fontFamily: ORG_PALETTE.font.family,
    fontWeight: ORG_PALETTE.font.weight.semibold,
    fontSize: "16px",
    color: tok.text.primary,
    letterSpacing: "-0.01em",
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "470px",
        minWidth: "330px",
        backgroundColor: tok.bg.card,
        borderRadius: ORG_PALETTE.radius.card,
        border: `1px solid ${tok.border.card}`,
        boxShadow: isDark ? "0 6px 28px rgba(0,0,0,0.3)" : "0 6px 20px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "sticky",
        top: "20px",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          padding: "20px 28px",
          borderBottom: `1px solid ${tok.border.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: isDark ? "#1c1c20" : "#f8fafc",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <LuSlidersHorizontal size={20} color={sectionIconColor} />
          <Typography sx={{
            fontFamily: ORG_PALETTE.font.family,
            fontWeight: ORG_PALETTE.font.weight.semibold,
            fontSize: "18px",
            color: tok.text.primary,
            letterSpacing: "-0.01em",
          }}>
            {t("Filters")}
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              sx={{
                height: 22,
                fontSize: "12px",
                fontWeight: 700,
                backgroundColor: isDark ? "#4ade80" : "#16a34a",
                color: "#ffffff",
              }}
            />
          )}
        </Box>

        {activeFiltersCount > 0 && (
          <Button
            size="small"
            onClick={onResetAll}
            startIcon={<LuRotateCcw size={13} />}
            sx={{
              textTransform: "none",
              fontFamily: ORG_PALETTE.font.family,
              fontSize: "14px",
              fontWeight: 500,
              color: isDark ? "#94a3b8" : "#64748b",
              "&:hover": { color: isDark ? "#ef4444" : "#dc2626" },
            }}
          >
            {t("Clear")}
          </Button>
        )}
      </Box>

      {/* ── Filter Body ── */}
      <Box sx={{ flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 190px)" }}>


        {/* 1. Manufacturer */}
        <Accordion defaultExpanded sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuBuilding2 size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("Manufacturer")}</Typography>
              {selectedManufacturers.length > 0 && (
                <Chip
                  label={selectedManufacturers.length}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("Search manufacturers...")}
              value={manufacturerSearch}
              onChange={(e) => setManufacturerSearch(e.target.value)}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  backgroundColor: isDark ? "#27272a" : "#f8fafc",
                  borderRadius: "6px",
                },
              }}
            />
            <Box sx={{ maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredManufacturers.map((mItem, idx) => {
                const isSelected = selectedManufacturers.includes(mItem.name);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleManufacturerToggle(mItem.name)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                      "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <SquareCheckBox label={mItem.name} checked={isSelected} onChange={() => handleManufacturerToggle(mItem.name)} />
                    </Box>
                    {mItem.count && (
                      <Chip
                        label={mItem.count}
                        size="small"
                        sx={{ height: 18, fontSize: "10px", backgroundColor: isDark ? "#27272a" : "#e2e8f0", mt: "2px" }}
                      />
                    )}
                  </Box>
                );
              })}
              {allManufacturers.length === 0 && (
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "14px", color: isDark ? "#64748b" : "#94a3b8", py: 1 }}>
                  {t("Loading manufacturers...")}
                </Typography>
              )}
            </Box>
            {!manufacturerSearch && allManufacturers.length > 10 && (
              <Button
                size="small"
                onClick={() => setShowAllManufacturers(!showAllManufacturers)}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: sectionIconColor,
                }}
              >
                {showAllManufacturers ? t("Show Less") : t("+ See all")}
              </Button>
            )}
          </AccordionDetails>
        </Accordion>

        {/* 2. Category */}
        <Accordion defaultExpanded sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuLayers size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("Category")}</Typography>
              {(selectedCategory || selectedSubCategory) && (
                <Chip
                  label={selectedSubCategory ? selectedSubCategory.label : selectedCategory.label}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: isDark ? "rgba(74,222,128,0.2)" : "#dcfce7", color: sectionIconColor, maxWidth: 100 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("Search categories...")}
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  backgroundColor: isDark ? "#27272a" : "#f8fafc",
                  borderRadius: "6px",
                },
              }}
            />
            <Box sx={{ maxHeight: "240px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredCategories.map((cat) => {
                const isCatSelected = selectedCategory?.key === cat.key;
                const isExpanded = expandedCategories.includes(cat.key);
                const hasSubCategories = cat.sub_categories && cat.sub_categories.length > 0;

                return (
                  <Box key={cat.key}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 0.8,
                        borderRadius: "6px",
                        cursor: "pointer",
                        backgroundColor: isCatSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                        "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                      }}
                    >
                      <Box
                        sx={{ flex: 1 }}
                        onClick={() => {
                          if (isCatSelected) {
                            setSelectedCategory(null);
                            setSelectedSubCategory(null);
                          } else {
                            setSelectedCategory(cat);
                            setSelectedSubCategory(null);
                          }
                        }}
                      >
                        <SquareCheckBox
                          label={cat.label}
                          checked={isCatSelected}
                          onChange={() => {}}
                        />
                      </Box>
                      {hasSubCategories && (
                        <IconButton
                          size="small"
                          onClick={(e) => toggleCategoryExpand(cat.key, e)}
                          sx={{
                            p: 0.5,
                            ml: 1,
                            borderRadius: "4px",
                            color: isDark ? "#94a3b8" : "#64748b",
                            "&:hover": {
                              backgroundColor: isDark ? "#3f3f46" : "#e2e8f0",
                              color: sectionIconColor,
                            },
                          }}
                        >
                          {isExpanded ? <LuChevronDown size={18} /> : <LuChevronRight size={18} />}
                        </IconButton>
                      )}
                    </Box>

                    {/* Subcategories (shown when expanded) */}
                    {hasSubCategories && isExpanded && (
                      <Box sx={{ pl: 3.5, mt: 0.75, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {cat.sub_categories.map((sub) => {
                          const isSubSelected = selectedSubCategory?.key === sub.key;
                          return (
                            <Box
                              key={sub.key}
                              onClick={() => setSelectedSubCategory(isSubSelected ? null : sub)}
                              sx={{
                                py: 0.6,
                                px: 1.5,
                                borderRadius: "4px",
                                cursor: "pointer",
                                backgroundColor: isSubSelected ? (isDark ? "rgba(74,222,128,0.08)" : "#f0fdf4") : "transparent",
                                "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                              }}
                            >
                              <SquareCheckBox
                                label={sub.label}
                                checked={isSubSelected}
                                onChange={() => setSelectedSubCategory(isSubSelected ? null : sub)}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 3. Normalized Unit */}
        <Accordion defaultExpanded sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuRuler size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("Normalized Unit")}</Typography>
              {selectedUnit && (
                <Chip
                  label={selectedUnit}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <FilterBoxGroup
              options={dynamicUnitOptions}
              selected={selectedUnit}
              onChange={setSelectedUnit}
              gridTemplateColumns="repeat(3, 1fr)"
              gap={1}
            />
          </AccordionDetails>
        </Accordion>

        {/* 4. EPD Standards */}
        <Accordion defaultExpanded sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuFileText size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("EPD Standards")}</Typography>
              {selectedStandards.length > 0 && (
                <Chip
                  label={selectedStandards.length}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {STANDARDS_LIST.map((std, idx) => {
                const isSelected = selectedStandards.includes(std);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleStandardToggle(std)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                      "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <SquareCheckBox
                        label={std}
                        checked={isSelected}
                        onChange={() => handleStandardToggle(std)}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 5. EPD Programs / Verification Org */}
        <Accordion sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuFileText size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("EPD Programs")}</Typography>
              {selectedPrograms.length > 0 && (
                <Chip
                  label={selectedPrograms.length}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("Search EPD programs...")}
              value={programSearch}
              onChange={(e) => setProgramSearch(e.target.value)}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  backgroundColor: isDark ? "#27272a" : "#f8fafc",
                  borderRadius: "6px",
                },
              }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredPrograms.map((prog, idx) => {
                const isSelected = selectedPrograms.includes(prog);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleProgramToggle(prog)}
                    sx={{
                      cursor: "pointer",
                      py: 0.8,
                      px: 1.5,
                      borderRadius: "6px",
                      backgroundColor: isSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                      "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <SquareCheckBox label={prog} checked={isSelected} onChange={() => handleProgramToggle(prog)} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {!programSearch && (
              <Button
                size="small"
                onClick={() => setShowAllPrograms(!showAllPrograms)}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: sectionIconColor,
                }}
              >
                {showAllPrograms ? t("Show Less") : t("+ See all (84)")}
              </Button>
            )}
          </AccordionDetails>
        </Accordion>

        {/* 6. Region */}
        <Accordion defaultExpanded sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuGlobe size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("Region")}</Typography>
              {selectedRegions.length > 0 && (
                <Chip
                  label={selectedRegions.length}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {REGION_LIST.map((reg, idx) => {
                const isSelected = selectedRegions.includes(reg);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleRegionToggle(reg)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                      "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <SquareCheckBox
                        label={reg}
                        checked={isSelected}
                        onChange={() => handleRegionToggle(reg)}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* 7. Country */}
        <Accordion defaultExpanded sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuGlobe size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("Country")}</Typography>
              {selectedCountries.length > 0 && (
                <Chip
                  label={selectedCountries.length}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("Search countries...")}
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LuSearch size={16} color={isDark ? "#94a3b8" : "#64748b"} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  backgroundColor: isDark ? "#27272a" : "#f8fafc",
                  borderRadius: "6px",
                },
              }}
            />
            <Box sx={{ maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {filteredCountries.map((c, idx) => {
                const isSelected = selectedCountries.includes(c.code);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleCountryToggle(c.code)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                      "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <SquareCheckBox
                        label={c.name}
                        checked={isSelected}
                        onChange={() => handleCountryToggle(c.code)}
                      />
                    </Box>
                    <Typography sx={{ fontSize: "17px", ml: 1, mt: "1px" }}>{c.flag}</Typography>
                  </Box>
                );
              })}
              {countryOptions.length === 0 && (
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "14px", color: isDark ? "#64748b" : "#94a3b8", py: 1 }}>
                  {t("No countries available")}
                </Typography>
              )}
            </Box>
            {!countrySearch && countryOptions.length > 8 && (
              <Button
                size="small"
                onClick={() => setShowAllCountries(!showAllCountries)}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: sectionIconColor,
                }}
              >
                {showAllCountries ? t("Show Less") : t("+ See all")}
              </Button>
            )}
          </AccordionDetails>
        </Accordion>

        {/* 8. Publication Year */}
        <Accordion sx={accordionStyle}>
          <AccordionSummary expandIcon={<LuChevronDown size={18} color={isDark ? "#94a3b8" : "#64748b"} />} sx={accordionSummaryStyle}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <LuCalendar size={18} color={sectionIconColor} />
              <Typography sx={sectionHeaderSx}>{t("Publication Year")}</Typography>
              {selectedYears.length > 0 && (
                <Chip
                  label={selectedYears.length}
                  size="small"
                  sx={{ height: 18, fontSize: "11px", backgroundColor: sectionIconColor, color: "#fff" }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3.5, pb: 3, pt: 0.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: "180px", overflowY: "auto" }}>
              {availableYears.map((yr, idx) => {
                const isSelected = selectedYears.includes(yr);
                return (
                  <Box
                    key={idx}
                    onClick={() => handleYearToggle(yr)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? (isDark ? "rgba(74,222,128,0.12)" : "#f0fdf4") : "transparent",
                      "&:hover": { backgroundColor: isDark ? "#27272a" : "#f1f5f9" },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <SquareCheckBox
                        label={String(yr)}
                        checked={isSelected}
                        onChange={() => handleYearToggle(yr)}
                      />
                    </Box>
                  </Box>
                );
              })}
              {availableYears.length === 0 && (
                <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontSize: "14px", color: isDark ? "#64748b" : "#94a3b8", py: 1 }}>
                  {t("No publication years available")}
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

      </Box>

      {/* ── Sticky Apply Filters Button ── */}
      <Box
        sx={{
          p: 2.5,
          borderTop: `1px solid ${tok.border.divider}`,
          backgroundColor: isDark ? "#1c1c20" : "#f8fafc",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={onApplyFilters}
          sx={{
            backgroundColor: isDark ? "#4ade80" : "#16a34a",
            color: "#ffffff",
            fontFamily: ORG_PALETTE.font.family,
            fontWeight: ORG_PALETTE.font.weight.semibold,
            fontSize: "15px",
            textTransform: "none",
            borderRadius: "10px",
            py: 1.5,
            letterSpacing: "0.01em",
            "&:hover": {
              backgroundColor: isDark ? "#22c55e" : "#15803d",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(22,163,74,0.3)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {t("Apply Filters")}
        </Button>
      </Box>
    </Box>
  );
};

export default EPDFiltersSidebar;
